import { Toolbar, Box, Stack, Divider } from "@mui/material";
import { CustomLogo } from "components/images/Logo";
import { useLocation, useParams } from "react-router-dom";
import { AppRoutes } from "../../types/enums";
import { ProfileButton } from "../profile";
import { GradientButton } from "../ui";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import { ResyncDataBtn } from "../ui/ResyncDataBtn";
import { useGetUserQuery, useLazyGetCampaignSummaryQuery } from "../../api";
import { useEffect } from "react";
import { pushUserDataToGTM } from "../../utils/pushUserDataToGTM";
import { NotificationBtn, ShareCampaignBtn } from "../ui/buttons";
import { DemoBlock } from "./DemoBlock";
import {
  useAppDispatch,
  useAppSelector,
  useGetUserShareCampaignRoles,
  useNotFoundRedirect,
  useScrollbarWidth,
} from "../../hooks";
import { setUserRole } from "../../features/companySlice";

export const Header = () => {
  const dispatch = useAppDispatch();
  const isModalOpen = useAppSelector((state) => state.company.isModalOpen);

  const { pathname } = useLocation();
  const { campaign_id } = useParams();

  const isAccountPlan =
    AppRoutes.ACCOUNT_PLAN.endsWith(pathname.split("/")[2]) ||
    pathname.includes("playbook");

  const scrollbarWidth = useScrollbarWidth({ trigger: isAccountPlan });

  const [getCampaignSummary, { data, error }] =
    useLazyGetCampaignSummaryQuery();
  const { data: user } = useGetUserQuery();

  const userRoles = useGetUserShareCampaignRoles(data?.user_roles || []);

  useEffect(() => {
    user && dispatch(setUserRole({ ...userRoles, userEmail: user.email }));
  }, [data?.user_roles, user]);

  useNotFoundRedirect({ error });

  useEffect(() => {
    if (user) {
      void pushUserDataToGTM(user.email_hash);
    }
  }, [user]);

  useEffect(() => {
    if (campaign_id) {
      getCampaignSummary({ campaign_id });
    }
  }, [pathname]);

  return (
    <>
      <Stack
        sx={{
          height:
            data?.is_demo ||
            (data?.user_roles && !userRoles.isEditor && !isAccountPlan)
              ? "100px"
              : "74px",
          background: "#F5F5F5",
          borderBottom: "1px solid #EAEAEA",
          boxShadow: "0px 5px 8.3px 0px #00000005",
          justifyContent: "center",
          position: "sticky",
          zIndex: 100,
          width: isModalOpen ? "100vw" : "100%",
          top: 0,
        }}
      >
        <Stack
          width="100%"
          sx={{
            px: { xs: 2, sm: 4, md: 5, xl: 9 },
          }}
        >
          <Toolbar style={{ padding: 0, justifyContent: "space-between" }}>
            <Link to={AppRoutes.CAMPAINGS}>
              <CustomLogo />
            </Link>
            <Stack
              flexDirection="row"
              gap="16px"
              sx={{
                ...(isModalOpen && { paddingRight: `${scrollbarWidth}px` }),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "16px",
                }}
              >
                <Stack flexDirection="row" gap="16px" alignItems="center">
                  {(userRoles.isOwner || userRoles.isAdmin) &&
                    user &&
                    campaign_id &&
                    !data?.is_demo && (
                      <ShareCampaignBtn userEmail={user.email} />
                    )}
                  {campaign_id && data && userRoles.isEditor && (
                    <ResyncDataBtn
                      key={campaign_id}
                      campaign_id={campaign_id}
                      crmIntegration={data.crm}
                      mismatches={data.mismatches}
                      isDisabled={data.is_demo}
                    />
                  )}

                  <Link to={AppRoutes.CREATE_CAMPAIGN}>
                    <GradientButton>
                      <AddIcon fontSize="small" />
                      Start New Campaign
                    </GradientButton>
                  </Link>
                </Stack>

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ alignSelf: "stretch" }}
                />
              </Box>
              <Stack flexDirection="row" gap="8px">
                {campaign_id && <NotificationBtn campaign_id={campaign_id} />}
                <ProfileButton />
              </Stack>
            </Stack>
          </Toolbar>
        </Stack>
        {campaign_id && data?.is_demo && (
          <DemoBlock
            isDemo={true}
            paddingRight={isModalOpen ? `${scrollbarWidth}px` : undefined}
          />
        )}
        {campaign_id &&
          data?.user_roles &&
          !userRoles.isEditor &&
          !data?.is_demo &&
          !isAccountPlan && (
            <DemoBlock
              isDemo={false}
              paddingRight={isModalOpen ? `${scrollbarWidth}px` : undefined}
            />
          )}
      </Stack>
    </>
  );
};
